import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import DropdownList from 'react-widgets/DropdownList';
import Multiselect from 'react-widgets/Multiselect';
import DynamicSelectSegment from '../DynamicSelectSegment';
import Custom from '../Multiselects/Custom';
import Default from '../Multiselects/Default';
import Button from '../Button';
import { useTranslation } from '../ChildrenContext';

const components = {
    Custom,
    Default,
    DynamicSelectSegment
};

// One filter (segment) row of a report page: filter type, values, include/exclude.
// Shared by /statistics/report and all /statistics/report-by-* pages.
// reportComponent: the page's Report*Component (getParams decides the values editor)
// translationKey: the page's translation section with groupParamsSegment (statistics, statisticsByUTM, ...)
// hideControls: common Apply/Reset below all rows -> the row shows only a "remove filter" button
const ReportSegmentsItem = ({
    segment: propSegment,
    exclusive: propExclusive,
    values: propValues,
    usedSegments = [],
    loading,
    onChangeSegment: propOnChangeSegment,
    onDelete: propOnDelete,
    onRemove: propOnRemove,
    onUpdateDate: propOnUpdateDate,
    index,
    canDelete,
    isPlaceholder,
    hideControls = false,
    reportComponent,
    segmentKeys = [],
    translationKey
}) => {
    const t = useTranslation();

    const getTypeMultiselect = useCallback((segment) => {
        const metricParams = (segment && reportComponent && reportComponent.getParams({key: segment, metricKey: 'GroupBy'})) || {};

        if (metricParams.format === 'ids') {
            return 'DynamicSelectSegment';
        } else if (metricParams.values) {
            return 'Default';
        } else {
            return 'Custom';
        }
    }, [reportComponent]);

    const [state, setState] = useState(() => ({
        segment: propSegment,
        exclusive: propExclusive,
        values: propValues,
        typeMultiselect: getTypeMultiselect(propSegment)
    }));

    useEffect(() => {
        setState(prevState => {
            const segmentChanged = propSegment !== undefined && propSegment !== prevState.segment;
            const exclusiveChanged = typeof propExclusive === 'boolean' && propExclusive !== prevState.exclusive;
            const valuesChanged = segmentChanged ? true : propValues !== prevState.values;

            if (!segmentChanged && !exclusiveChanged && !valuesChanged) {
                return prevState;
            }

            const nextSegment = segmentChanged ? propSegment : prevState.segment;
            return {
                ...prevState,
                segment: nextSegment,
                exclusive: exclusiveChanged ? propExclusive : prevState.exclusive,
                values: segmentChanged ? propValues : prevState.values,
                typeMultiselect: getTypeMultiselect(nextSegment)
            };
        });
    }, [propSegment, propExclusive, propValues, getTypeMultiselect]);

    const onChange = useCallback((customValues) => {
        const nextValues = customValues || state.values;
        propOnChangeSegment && propOnChangeSegment({
            segment: state.segment,
            exclusive: state.exclusive,
            values: (nextValues || []).map((item) => (item && typeof item === 'object' ? item.value : item))
        }, index);
    }, [state.segment, state.exclusive, state.values, index, propOnChangeSegment]);

    const onChangeSegment = useCallback((value) => {
        if (!value) {
            return;
        }
        setState(prevState => ({
            ...prevState,
            segment: value.value,
            values: [],
            exclusive: false,
            typeMultiselect: getTypeMultiselect(value.value)
        }));
    }, [getTypeMultiselect]);

    useEffect(() => {
        if (state.segment) {
            onChange();
        }
    }, [state.segment, state.exclusive, state.values]);

    const onExclusiveChange = useCallback((status) => {
        setState(prevState => ({
            ...prevState,
            exclusive: status.value === 'exclude'
        }));
    }, []);

    const onChangeValues = useCallback((value) => {
        setState(prevState => ({
            ...prevState,
            values: value
        }));
    }, []);

    const onDelete = useCallback(() => {
        propOnDelete && propOnDelete(index);
    }, [index, propOnDelete]);

    const onRemove = useCallback(() => {
        propOnRemove && propOnRemove(index);
    }, [index, propOnRemove]);

    const onApply = useCallback(() => {
        propOnUpdateDate && propOnUpdateDate();
    }, [propOnUpdateDate]);

    const groupParamsSegment = useMemo(() => {
        const params = (t && t[translationKey] && t[translationKey].groupParamsSegment) || {};
        const hasTranslations = Object.keys(params).length > 0;
        const keys = hasTranslations ? Object.keys(params) : (segmentKeys || []);

        return keys.map((key) => ({ title: hasTranslations ? params[key] : key, value: key }));
    }, [t, translationKey, segmentKeys]);

    const filteredParams = useMemo(() => {
        const selectedSet = new Set(usedSegments || []);

        return (groupParamsSegment || []).filter(opt => {
            if (!opt.value) {
                return true;
            }

            if (opt.value === state.segment) {
                return true;
            }

            return !selectedSet.has(opt.value);
        });
    }, [groupParamsSegment, state.segment, usedSegments]);

    const staticNames = t?.reportPage || {};
    const staticActions = t?.actions || {};
    const removeTitle = staticNames.removeFilter || staticActions.removeFilter || 'Remove filter';

    const Multiselector = components[state.typeMultiselect] || Custom;
    const exclusiveOptions = [
        {title: staticNames.excludeSegmentTitle, value: 'exclude'},
        {title: staticNames.includeSegmentTitle, value: 'include'}
    ];

    return (
        <div className="row flex-lg-nowrap flex-wrap">
            <div className="table-filter__item">
                <label className="input-label"> {state.segment ? staticNames.filter : staticNames.addFilter} </label>
                <DropdownList
                    className={'rw-dropdownlist-select'}
                    dataKey="value"
                    valueField="value"
                    textField="title"
                    data={filteredParams}
                    value={filteredParams.find((opt) => opt.value === state.segment) || null}
                    placeholder={staticNames.selectFilter}
                    disabled={isPlaceholder && !!propSegment}
                    onChange={onChangeSegment}
                />
            </div>

            <div className="table-filter__item custom-input">
                <label className="input-label"> {staticNames.addMeaning} </label>
                {state.segment
                    ? (
                        state.typeMultiselect === 'DynamicSelectSegment'
                            ? <DynamicSelectSegment segment={state.segment} values={state.values} multiselect={true} onAction={onChangeValues} />
                            : <Multiselector segment={state.segment} values={state.values} onAction={onChangeValues} />
                    )
                    : <Multiselect disabled={true} />
                }
            </div>

            <div className="table-filter__item table-filter__item2x">
                <label className="input-label"> {staticNames.meaning} </label>
                <DropdownList
                    className={'rw-dropdownlist-select'}
                    dataKey="value"
                    valueField="value"
                    textField="title"
                    data={exclusiveOptions}
                    value={exclusiveOptions[state.exclusive ? 0 : 1]}
                    placeholder={state.exclusive ? staticNames.excludeSegment : staticNames.includeSegment}
                    onChange={onExclusiveChange}
                    disabled={!state.segment}
                />
            </div>

            {hideControls
                ? (state.segment
                    ? (
                        <div className="table-filter__item table-filter__item--remove">
                            <button
                                type="button"
                                className="btn reset-btn table-filter__remove"
                                title={removeTitle}
                                aria-label={removeTitle}
                                onClick={onRemove}
                            >
                                &times;
                            </button>
                        </div>
                    )
                    : null)
                : (
                    <div className="statistics__btn-wrap">
                        <Button
                            extraClass={{'apply-btn': true}}
                            loading={loading}
                            text={staticActions.apply}
                            onClick={onApply}
                            disabled={!state.segment}
                        />
                        <Button
                            extraClass={{'reset-btn': true}}
                            text={staticActions.reset}
                            onClick={onDelete}
                            disabled={!state.segment || !canDelete}
                        />
                    </div>
                )
            }
        </div>
    );
};

ReportSegmentsItem.propTypes = {
    values: PropTypes.array,
    segment: PropTypes.string,
    exclusive: PropTypes.bool,
    usedSegments: PropTypes.array,
    loading: PropTypes.bool,
    onChangeSegment: PropTypes.func,
    onDelete: PropTypes.func,
    onRemove: PropTypes.func,
    onUpdateDate: PropTypes.func,
    hideControls: PropTypes.bool,
    index: PropTypes.number,
    canDelete: PropTypes.bool,
    isPlaceholder: PropTypes.bool,
    reportComponent: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    segmentKeys: PropTypes.array,
    translationKey: PropTypes.string
};

ReportSegmentsItem.defaultProps = {
    values: [],
    segment: '',
    exclusive: false,
    usedSegments: [],
    loading: false,
    onChangeSegment: () => {},
    onDelete: () => {},
    onRemove: () => {},
    onUpdateDate: () => {},
    index: 0,
    canDelete: true,
    isPlaceholder: false,
    translationKey: 'statistics'
};

export default ReportSegmentsItem;
